import { Box, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import theme from "../../../../theme";
import { Field, FieldArray, Form, Formik } from "formik";
import { Inputs } from "../../../formInputs";
import { useDispatch, useSelector } from "../../../../store";
import { debounce } from "lodash";
import { searchJobByType } from "../../../../slices/job";
import SearchParams, { defaultParams } from "../../../../types/searchParams";
import { Base } from "../../../base";
import {
  getJobAttachments,
  postAttachment,
} from "../../../../slices/attachment";
import { getAttachmentTypes } from "../../../../slices/attachmentType";
import Attachment from "../../../../types/attachment";
import * as Yup from "yup";

interface Props {
  id: number;
  newUpload: Attachment[];
  setNewUpload: React.Dispatch<React.SetStateAction<Attachment[]>>;
  setFileTypeModal: React.Dispatch<React.SetStateAction<boolean>>;
  setFileLength: React.Dispatch<React.SetStateAction<number>>;
}

const JobAttachmentCopy: React.FC<Props> = (props) => {
  const { id, newUpload, setNewUpload, setFileTypeModal, setFileLength } =
    props;
  const dispatch = useDispatch();
  const [jobByTypeFilter, setJobByTypeFilter] =
    useState<SearchParams>(defaultParams);
  const { jobByTypeSearchResults, job } = useSelector((store) => store.job);
  const { status: attachmentStatus } = useSelector(
    (store) => store.attachments,
  );
  const { attachmentTypes } = useSelector((store) => store.attachmentType);

  useEffect(() => {
    dispatch(searchJobByType(jobByTypeFilter));
  }, [dispatch, jobByTypeFilter]);

  useEffect(() => {
    dispatch(getAttachmentTypes());
  }, [dispatch]);

  const updateJobSearch = debounce((query: string, filter: number) => {
    setJobByTypeFilter({
      ...jobByTypeFilter,
      query,
      filters: [`jobtype:${filter}`],
    });
  }, 500);

  var validationSchema = Yup.object().shape({
    name: Yup.string().required("Required").typeError("Required"),
  });

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        width: { sm: 500, xs: "100%" },
        borderRadius: "10px",
        padding: "15px",
      }}
    >
      <Typography variant="h3" textAlign={"center"}>
        Select Attachment Type
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Formik
        initialValues={{ attachments: newUpload }}
        onSubmit={() => {}}
        validationSchema={validationSchema}
      >
        {({ values }) => (
          <Form noValidate>
            <FieldArray
              name="attachments"
              render={() => (
                <Grid container height={250} sx={{ overflowY: "scroll" }}>
                  <Grid size={12}>
                    {newUpload.map((u, i) => {
                      return (
                        <Grid key={i} pb={1}>
                          <Grid mb={1}>{u.name}</Grid>
                          <Grid container spacing={1}>
                            <Grid
                              size={{
                                xs: 12,
                                sm:
                                  newUpload[i].attachmentType
                                    ?.attachmentCopyType?.id === 1 &&
                                  newUpload[i].attachmentType?.copyJobType
                                    ?.id !== job.jobType?.id
                                    ? 6
                                    : 12,
                              }}
                            >
                              <Field
                                component={Inputs.Text}
                                name={`attachments.${i}.name`}
                                label="Name"
                                required
                              />
                              <Field
                                component={Inputs.Dropdown}
                                options={attachmentTypes}
                                staticFilter
                                name={`attachments.${i}.attachmentType`}
                                label="Attachment Type"
                                onSelected={(e: any, v: any) => {
                                  const newArray = [...newUpload];
                                  newArray[i].attachmentType = v;
                                  setNewUpload(newArray);
                                  if (
                                    newArray[i].attachmentType?.copyJobType?.id
                                  ) {
                                    updateJobSearch(
                                      "",
                                      newArray[i]?.attachmentType?.copyJobType
                                        ?.id!,
                                    );
                                  }
                                }}
                              />
                              <Field
                                component={Inputs.TextArea}
                                name={`attachments.${i}.note`}
                                label="Note"
                              />
                            </Grid>
                            {newUpload[i].attachmentType?.attachmentCopyType
                              ?.id === 1 &&
                              newUpload[i].attachmentType?.copyJobType?.id !==
                                job.jobType?.id && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Field
                                    component={Inputs.Dropdown}
                                    options={
                                      jobByTypeSearchResults?.results || []
                                    }
                                    name={`values.${i}.copyTo`}
                                    label="Copy To?"
                                    onSelected={(e: any, v: any) => {
                                      const newArray = [...newUpload];
                                      newArray[i].copyTo = v;
                                      setNewUpload(newArray);
                                    }}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      updateJobSearch(
                                        e.currentTarget.value,
                                        newUpload[i]?.attachmentType
                                          ?.copyJobType?.id!,
                                      );
                                    }}
                                    onFocus={() => {
                                      updateJobSearch(
                                        "",
                                        newUpload[i]?.attachmentType
                                          ?.copyJobType?.id!,
                                      );
                                    }}
                                  />
                                </Grid>
                              )}
                          </Grid>
                          <Divider />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              )}
            />
            <Grid
              container
              direction={"row"}
              justifyContent={"space-between"}
              my={1}
            >
              <Base.Button
                text="Cancel"
                type="secondary"
                onClick={() => {
                  setFileTypeModal(false);
                  setNewUpload([]);
                  setFileLength(0);
                }}
              />
              <Base.Button
                text="Upload"
                disabled={
                  attachmentStatus === "loading" ||
                  newUpload.some((a) => !a.name)
                }
                onClick={() => {
                  let upload = 0;
                  values.attachments.forEach((u, i) => {
                    dispatch(
                      postAttachment({ ...u, job: { id: u.jobId } }),
                    ).then((result) => {
                      dispatch(getJobAttachments(id));
                      if (result.meta.requestStatus === "fulfilled") {
                        upload = upload + 1;
                      }
                    });
                    if (i === upload) {
                      setFileTypeModal(false);
                      setNewUpload([]);
                      setFileLength(0);
                    }
                  });
                }}
              />
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default JobAttachmentCopy;
